import { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Clock, MessageSquareWarning, ArrowDown } from 'lucide-react';
import { api } from '../lib/api';

const formatSeconds = (s) => {
  if (!s || s <= 0) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m}m ${rem}s`;
};

const TrendBadge = ({ pct }) => {
  if (pct == null || pct === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-500" data-testid="trend-flat">
        <Minus className="w-3 h-3" /> 0%
      </span>
    );
  }
  const up = pct > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-600'}`}
      data-testid={up ? 'trend-up' : 'trend-down'}
    >
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? '+' : ''}{pct}%
    </span>
  );
};

const StatPill = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

// Determine the colour for the biggest drop-off row so it's visually loud.
const findWorstDropIndex = (rows) => {
  let worst = -1;
  let worstPct = 0;
  rows.forEach((r, i) => {
    if (i === 0) return; // first step has no conversion ratio
    if (r.drop_off_pct > worstPct) {
      worstPct = r.drop_off_pct;
      worst = i;
    }
  });
  return worst;
};

export default function FunnelAnalytics({ adminToken }) {
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.adminGetFunnelSummary(adminToken, windowDays)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Failed to load funnel summary'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [adminToken, windowDays]);

  if (loading && !data) {
    return <div className="text-sm text-slate-500 py-8 text-center" data-testid="funnel-loading">Loading funnel…</div>;
  }
  if (error) {
    return <div className="text-sm text-rose-600 py-8 text-center" data-testid="funnel-error">{error}</div>;
  }
  if (!data) return null;

  const worstIdx = findWorstDropIndex(data.funnel);
  const { engagement } = data;

  return (
    <div className="space-y-6" data-testid="funnel-analytics">
      {/* Window selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Registration Funnel</div>
          <div className="text-xs text-slate-500">Last {windowDays} days · trend compared vs previous {windowDays} days</div>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="funnel-window-selector">
          {[1, 7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${windowDays === d ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`funnel-window-${d}`}
            >
              {d === 1 ? '24h' : `${d}d`}
            </button>
          ))}
        </div>
      </div>

      {/* Engagement metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatPill testid="stat-avg-landing-time" label="Avg Landing Time" value={formatSeconds(engagement.avg_landing_seconds)} sub="Time on landing page" />
        <StatPill testid="stat-avg-demo-time" label="Avg Demo Watch" value={formatSeconds(engagement.avg_demo_seconds)} sub="Per demo opened" />
        <StatPill testid="stat-avg-register-time" label="Avg Register Time" value={formatSeconds(engagement.avg_register_seconds)} sub="Time on register page" />
        <StatPill testid="stat-avg-register-complete" label="Avg Time to Submit" value={formatSeconds(engagement.avg_register_complete_seconds)} sub="Opened → Submitted" />
        <StatPill
          testid="stat-exit-survey"
          label="Exit Survey Rate"
          value={`${engagement.exit_survey_rate_pct || 0}%`}
          sub={`${engagement.exit_survey_submitted} responses`}
        />
      </div>

      {/* Worst-drop callout */}
      {worstIdx > 0 && data.funnel[worstIdx].drop_off_pct >= 30 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3" data-testid="funnel-worst-drop">
          <MessageSquareWarning className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-rose-900">Biggest drop-off: {data.funnel[worstIdx - 1].label} → {data.funnel[worstIdx].label}</div>
            <div className="text-xs text-rose-700 mt-0.5">
              <strong>{data.funnel[worstIdx].drop_off_pct}%</strong> of users who reach
              <em> {data.funnel[worstIdx - 1].label.toLowerCase()}</em> never reach
              <em> {data.funnel[worstIdx].label.toLowerCase()}</em>. Optimising this step has the highest ROI.
            </div>
          </div>
        </div>
      )}

      {/* The visual funnel */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Step-by-step conversion</div>
          <div className="text-[11px] text-slate-500">Width ∝ users in step · width scaled to top of funnel</div>
        </div>
        <div className="p-4 space-y-1.5">
          {data.funnel.map((row, idx) => {
            const widthPct = data.funnel[0].count > 0 ? Math.max(8, (row.count / data.funnel[0].count) * 100) : 8;
            const isWorst = idx === worstIdx && row.drop_off_pct >= 30;
            return (
              <div key={row.key} data-testid={`funnel-step-${row.key}`}>
                {idx > 0 && (
                  <div className="flex justify-center -my-1">
                    <ArrowDown className={`w-3 h-3 ${isWorst ? 'text-rose-500' : 'text-slate-300'}`} />
                  </div>
                )}
                <div className="flex items-center gap-3">
                  {/* Step number */}
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    idx === 0 ? 'bg-blue-100 text-blue-700' :
                    isWorst ? 'bg-rose-100 text-rose-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {idx + 1}
                  </div>
                  {/* Bar */}
                  <div className="flex-1 relative">
                    <div
                      className={`h-10 rounded-lg flex items-center px-3 transition-all ${
                        idx === 0 ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' :
                        isWorst ? 'bg-gradient-to-r from-rose-400 to-rose-500 text-white' :
                        'bg-gradient-to-r from-slate-200 to-slate-300 text-slate-800'
                      }`}
                      style={{ width: `${widthPct}%`, minWidth: '160px' }}
                    >
                      <div className="text-xs font-semibold truncate">{row.label}</div>
                    </div>
                  </div>
                  {/* Stats */}
                  <div className="flex items-center gap-3 shrink-0 min-w-[260px] justify-end text-right">
                    <div>
                      <div className="text-base font-bold text-slate-900 font-mono">{row.count}</div>
                      <div className="text-[10px] text-slate-500">users</div>
                    </div>
                    <div className="w-16">
                      <div className={`text-sm font-bold font-mono ${
                        idx === 0 ? 'text-blue-700' :
                        row.conversion_pct >= 60 ? 'text-emerald-600' :
                        row.conversion_pct >= 30 ? 'text-amber-600' :
                        'text-rose-600'
                      }`}>{idx === 0 ? '—' : `${row.conversion_pct}%`}</div>
                      <div className="text-[10px] text-slate-500">conv.</div>
                    </div>
                    <div className="w-16">
                      <div className={`text-sm font-bold font-mono ${idx === 0 ? 'text-slate-400' : row.drop_off_pct >= 50 ? 'text-rose-600' : 'text-slate-700'}`}>
                        {idx === 0 ? '—' : `${row.drop_off_pct}%`}
                      </div>
                      <div className="text-[10px] text-slate-500">drop</div>
                    </div>
                    <div className="w-14">
                      <TrendBadge pct={row.trend_pct} />
                      <div className="text-[10px] text-slate-500 mt-0.5">vs prev</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Registration micro-funnel */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="register-micro-funnel">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-500" />
          <div className="text-sm font-semibold text-slate-700">Registration micro-funnel</div>
          <div className="text-[11px] text-slate-500 ml-2">Find exactly where users abandon the signup form</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-slate-100 text-left text-slate-600">
              <tr>
                <th className="px-4 py-2 font-semibold">Step</th>
                <th className="px-4 py-2 font-semibold text-right">Users</th>
                <th className="px-4 py-2 font-semibold text-right">Conversion</th>
                <th className="px-4 py-2 font-semibold text-right">Drop-off</th>
                <th className="px-4 py-2 font-semibold text-right">Trend</th>
              </tr>
            </thead>
            <tbody>
              {data.register_micro_funnel.map((row, idx) => (
                <tr key={row.key} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`micro-step-${row.key}`}>
                  <td className="px-4 py-2.5 text-slate-800 font-medium">
                    <span className="inline-block w-5 text-slate-400 mr-1">{idx + 1}.</span>
                    {row.label}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold">{row.count}</td>
                  <td className={`px-4 py-2.5 text-right font-mono ${idx === 0 ? 'text-slate-400' : row.conversion_pct >= 60 ? 'text-emerald-600' : row.conversion_pct >= 30 ? 'text-amber-600' : 'text-rose-600'}`}>
                    {idx === 0 ? '—' : `${row.conversion_pct}%`}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono ${idx === 0 ? 'text-slate-400' : row.drop_off_pct >= 50 ? 'text-rose-600' : 'text-slate-700'}`}>
                    {idx === 0 ? '—' : `${row.drop_off_pct}%`}
                  </td>
                  <td className="px-4 py-2.5 text-right"><TrendBadge pct={row.trend_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-slate-500 leading-relaxed">
        <strong>How to read this:</strong> <em>Conversion %</em> is the share of users at the previous step who reached the current one.
        <em> Drop-off %</em> is the inverse. The highlighted row is your biggest leak — fix that first, the rest stays the same.
        <em> Trend</em> compares this {windowDays}-day window to the previous {windowDays}-day window.
      </div>
    </div>
  );
}
